import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Sparkles, Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

export default function Membership() {
  const { waLink } = useSettings();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [p, m] = await Promise.all([
        api.get("/memberships/plans"),
        user && user !== false ? api.get("/memberships/me") : Promise.resolve({ data: null }),
      ]);
      setPlans(p.data);
      setCurrent(m.data);
    })();
  }, [user]);

  const subscribe = async (planId) => {
    if (!user || user === false) { nav("/login"); return; }
    setBusy(true);
    try {
      await api.post("/memberships/subscribe", { plan_id: planId, period });
      const m = await api.get("/memberships/me");
      setCurrent(m.data);
      await refreshMe();
      toast.success(`Welcome to the ${planId.toUpperCase()} tribe! 🕉️`);
    } catch { toast.error("Subscription failed. Try again."); }
    setBusy(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Membership</div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tighter">
          Choose your <span className="italic gold-gradient">sadhana.</span>
        </h1>
        <p className="text-[#A0A4AB] mt-4 max-w-lg mx-auto">
          Unlock unlimited kundalis, priority astrologers, and free monthly poojas.
        </p>

        <div className="inline-flex items-center gap-2 mt-8 p-1 rounded-full border border-white/10 bg-[#0A0C10]">
          {["monthly", "yearly"].map((p) => (
            <button
              key={p}
              data-testid={`period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-5 py-2 rounded-full text-sm transition capitalize ${
                period === p ? "bg-[#D4AF37] text-black" : "text-[#A0A4AB] hover:text-white"
              }`}
            >
              {p} {p === "yearly" && <span className="text-[10px] text-emerald-400 ml-1">Save 15%</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const price = period === "monthly" ? plan.price_monthly : plan.price_yearly;
          const isCurrent = current?.plan_id === plan.id;
          return (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`relative p-8 rounded-2xl transition-all ${
                plan.highlight
                  ? "bg-gradient-to-b from-[#D4AF37]/20 to-[#0A0C10] border-2 border-[#D4AF37] scale-105 shadow-2xl"
                  : "bg-[#0A0C10] border border-white/10 hover:border-white/25"
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest font-medium">
                  {plan.tag}
                </div>
              )}
              {!plan.highlight && (
                <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB] mb-4">{plan.tag}</div>
              )}
              <div className="flex items-center gap-3">
                {plan.id === "seeker" && <Star className="text-[#A0A4AB]" size={22}/>}
                {plan.id === "sadhak" && <Sparkles className="text-[#D4AF37]" size={22}/>}
                {plan.id === "mahasadhak" && <Crown className="text-[#FF5A36]" size={22}/>}
                <div className="font-serif text-3xl">{plan.name}</div>
              </div>
              <div className="mt-6">
                <span className="font-serif text-5xl gold-gradient">₹{price}</span>
                <span className="text-sm text-[#A0A4AB] ml-2">/ {period === "monthly" ? "mo" : "yr"}</span>
              </div>
              <ul className="space-y-3 mt-8">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#EAE6DF]">
                    <Check size={14} className="text-emerald-400 mt-0.5 flex-none"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${plan.id}`}
                onClick={() => subscribe(plan.id)}
                disabled={busy || isCurrent || (plan.id === "seeker" && !current)}
                className={`w-full mt-8 py-3 rounded-full font-medium transition disabled:opacity-60 ${
                  plan.highlight ? "bg-[#D4AF37] text-black hover:bg-[#E5C865]"
                                 : "border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {isCurrent ? "✓ Current plan" : plan.id === "seeker" ? "Start Free" : `Choose ${plan.name}`}
              </button>
              {plan.id !== "seeker" && (
                <a
                  href={waLink(
                    `Namaste 🙏  I want to subscribe to the ${plan.name} plan (₹${price}/${period === "monthly" ? "month" : "year"}) on Vedacosmos. Please help me get started.`
                  )}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`wa-plan-${plan.id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 text-sm hover:bg-emerald-500/20 transition"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Or chat on WhatsApp
                </a>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-16 text-center text-xs text-[#6E737D]">
        Demo mode — no real charge. Real payments via Stripe/Razorpay coming soon.
      </div>
    </div>
  );
}
